import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';
import App from './App.jsx';
import './index.css';

// CRITICAL: Enhanced iOS Safari 18+ detection and optimization
const userAgent = navigator.userAgent || navigator.vendor || window.opera;
const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(userAgent);
const isSafari = /^((?!chrome|android).)*safari/i.test(userAgent) || /iPhone|iPad|iPod/i.test(userAgent);
const isIOS = /iPad|iPhone|iPod/.test(userAgent) && !window.MSStream;
const isIOSSafari = isIOS && /Safari/.test(userAgent) && !/CriOS|FxiOS|OPiOS|mercury/.test(userAgent);

// SUPER CRITICAL: iOS Safari 18+ specific optimizations
if (isIOSSafari || isMobile || isSafari) {
  console.log('🍎 iOS Safari detected - applying optimizations...');
  
  // Add iOS Safari class to document
  document.documentElement.classList.add('ios-safari', 'mobile-device');
  document.body.classList.add('safari-optimized', 'mobile-optimized');
  
  // CRITICAL: Viewport optimization for iOS Safari 18+
  const viewport = document.querySelector('meta[name="viewport"]');
  if (viewport) {
    viewport.setAttribute('content', 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover, shrink-to-fit=no');
  } else {
    // Create viewport meta if it doesn't exist
    const meta = document.createElement('meta');
    meta.name = 'viewport';
    meta.content = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover, shrink-to-fit=no';
    document.head.appendChild(meta);
  }
  
  // CRITICAL: Prevent zoom on input focus for iOS Safari 18+
  const preventZoomOnFocus = () => {
    const inputs = document.querySelectorAll('input, select, textarea');
    inputs.forEach(input => {
      if (input.style.fontSize !== '16px') {
        input.style.fontSize = '16px';
      }
      input.style.webkitTextSizeAdjust = '100%';
      input.style.textSizeAdjust = '100%';
    });
  };
  
  // Run on DOM ready and mutations
  document.addEventListener('DOMContentLoaded', preventZoomOnFocus);
  
  // MutationObserver to catch dynamically added inputs
  const observer = new MutationObserver(() => {
    preventZoomOnFocus();
  });
  observer.observe(document.body, { childList: true, subtree: true });
  
  // CRITICAL: Enhanced touch event handling for iOS Safari 18+
  document.addEventListener('touchstart', function(e) {
    // Add visual feedback for touches
    const target = e.target.closest('button, a, [role="button"], .mobile-tap, .clickable');
    if (target) {
      target.style.webkitTapHighlightColor = 'rgba(239, 68, 68, 0.7)';
      target.style.transform = 'translateZ(0) scale(0.98)';
      
      // Reset after touch
      setTimeout(() => {
        target.style.transform = 'translateZ(0) scale(1)';
      }, 150);
    }
  }, { passive: true });
  
  // CRITICAL: Force all interactive elements to be tappable
  const enhanceInteractiveElements = () => {
    const interactiveSelectors = [
      'button',
      'a',
      '[role="button"]',
      '[onclick]',
      '.mobile-tap',
      '.clickable',
      '.portal-card',
      'input',
      'select',
      'textarea'
    ];
    
    interactiveSelectors.forEach(selector => {
      document.querySelectorAll(selector).forEach(element => {
        // Force iOS Safari tappability
        element.style.webkitTapHighlightColor = 'rgba(239, 68, 68, 0.6)';
        element.style.touchAction = 'manipulation';
        element.style.webkitTouchAction = 'manipulation';
        element.style.cursor = 'pointer';
        element.style.position = 'relative';
        element.style.zIndex = '1';
        element.style.transform = 'translateZ(0)';
        element.style.webkitTransform = 'translateZ(0)';
        
        // Ensure minimum touch target size
        if (element.offsetHeight < 44) {
          element.style.minHeight = '44px';
        }
        if (element.offsetWidth < 44) {
          element.style.minWidth = '44px';
        }
      });
    });
  };
  
  // Run enhancement on load and mutations
  document.addEventListener('DOMContentLoaded', enhanceInteractiveElements);
  setTimeout(enhanceInteractiveElements, 100);
  setTimeout(enhanceInteractiveElements, 500);
  setTimeout(enhanceInteractiveElements, 1000);
  
  // CRITICAL: iOS Safari performance optimizations
  document.addEventListener('DOMContentLoaded', () => {
    // Force hardware acceleration on all elements
    document.querySelectorAll('*').forEach(element => {
      if (element.tagName !== 'SCRIPT' && element.tagName !== 'STYLE') {
        element.style.webkitBackfaceVisibility = 'hidden';
        element.style.backfaceVisibility = 'hidden';
        element.style.webkitTransform = 'translateZ(0)';
        element.style.transform = 'translateZ(0)';
      }
    });
    
    // Optimize scrolling
    document.body.style.webkitOverflowScrolling = 'touch';
    document.body.style.overflowScrolling = 'touch';
  });
  
  // CRITICAL: iOS Safari 18+ click event enhancement
  document.addEventListener('click', function(e) {
    const target = e.target.closest('button, a, [role="button"], .mobile-tap, .clickable');
    if (target) {
      // Add haptic feedback if available
      if (navigator.vibrate) {
        navigator.vibrate(50);
      }
      
      // Ensure the click is processed
      e.stopPropagation();
      
      // Force immediate visual feedback
      target.style.webkitTapHighlightColor = 'rgba(239, 68, 68, 0.8)';
      setTimeout(() => {
        target.style.webkitTapHighlightColor = 'rgba(239, 68, 68, 0.6)';
      }, 200);
    }
  }, true);
  
  // CRITICAL: iOS Safari window resize handler
  window.addEventListener('resize', () => {
    // Re-run optimizations on orientation change
    setTimeout(enhanceInteractiveElements, 100);
    setTimeout(preventZoomOnFocus, 100);
  });
  
  // Disable hover effects on mobile to improve performance
  const style = document.createElement('style');
  style.textContent = `
    @media (hover: none) and (pointer: coarse) {
      *:hover {
        transition: none !important;
      }
    }
  `;
  document.head.appendChild(style);
}

// Performance observer for monitoring (iOS Safari compatible)
if ('PerformanceObserver' in window) {
  try {
    const observer = new PerformanceObserver((list) => {
      for (const entry of list.getEntries()) {
        if (entry.entryType === 'largest-contentful-paint') {
          // Log performance metrics in development
          if (import.meta.env.DEV) {
            console.log('📊 LCP:', entry.startTime);
            if (isIOSSafari) {
              console.log('🍎 iOS Safari performance optimized');
            }
          }
        }
      }
    });
    observer.observe({ entryTypes: ['largest-contentful-paint'] });
  } catch (e) {
    // Ignore if performance observer fails
    console.log('⚠️ Performance observer not supported');
  }
}

// CRITICAL: iOS Safari app initialization
const initApp = () => {
  const root = createRoot(document.getElementById('root'));
  root.render(
    <StrictMode>
      <App />
    </StrictMode>
  );
  
  // Post-render iOS Safari optimizations
  if (isIOSSafari) {
    setTimeout(() => {
      console.log('🚀 iOS Safari optimizations complete');
      
      // Final enhancement pass
      document.querySelectorAll('button, a, [role="button"]').forEach(element => {
        element.style.webkitTapHighlightColor = 'rgba(239, 68, 68, 0.6)';
        element.style.touchAction = 'manipulation';
        element.addEventListener('touchstart', () => {}, { passive: true });
      });
    }, 1000);
  }
};

// Initialize the app
initApp();